// Dose timing pattern analysis.
//
// Pure functions over a list of dose records. Used by the
// /api/taper/tomorrow endpoint to surface "you typically dose around
// these times" + scaled per-slot mg for tomorrow's plan target.
//
// Nothing in here is medical advice. The output is descriptive of the
// user's own logged history; the calling endpoint pairs it with the
// already-stored taper_plan target and a clear disclaimer.

export interface DoseRecord {
  /** ISO8601 UTC timestamp, as stored in the doses table. */
  ts: string;
  /** Computed mg of 7-OH for the dose. */
  computedMg7oh: number;
}

export interface FindHabitualOptions {
  /** IANA timezone the user lives in, e.g. 'America/Los_Angeles'. */
  tz: string;
  /** Look back this many days from `now`. Default 14. */
  daysBack?: number;
  /** Reference "now" — defaults to the system clock; injected for tests. */
  now?: Date;
  /** A slot must appear on at least this many distinct days to count. Default 2. */
  minOccurrences?: number;
  /** Slot width in minutes. Default 30. */
  slotMinutes?: number;
  /** Cap on the number of peaks returned. Default 8. */
  maxPeaks?: number;
}

export interface TimingPeak {
  /** Local-time clock label "HH:MM" rounded to the slot center. */
  time: string;
  /** Fraction of the user's average daily mg that this slot represents. 0..1. */
  shareOfDailyMg: number;
  /** Distinct days this slot was dosed in. */
  sampleSize: number;
  /** Total mg observed in this slot over the window. */
  totalMg: number;
}

export type SlotConfidence = 'high' | 'medium' | 'low';

export interface ScheduleSlot {
  time: string;
  mg: number;
  confidence: SlotConfidence;
}

/**
 * Inspect a flat list of doses and surface the user's habitual clock-time
 * peaks in their local timezone.
 *
 * Algorithm:
 * 1. Filter to doses within `[now - daysBack, now]`.
 * 2. For each dose, convert to the user's TZ and bin into a slot of
 *    `slotMinutes` width (e.g., 30 min → 48 slots/day).
 * 3. Drop slots that appear on fewer than `minOccurrences` distinct days.
 * 4. Convert each surviving slot's total observed mg into a share of the
 *    user's average daily mg over the window.
 * 5. Sort by descending `totalMg`, take top `maxPeaks`.
 */
export function findHabitualDoseTimes(
  doses: readonly DoseRecord[],
  opts: FindHabitualOptions,
): TimingPeak[] {
  const tz = opts.tz;
  const daysBack = opts.daysBack ?? 14;
  const slotMinutes = opts.slotMinutes ?? 30;
  const minOccurrences = opts.minOccurrences ?? 2;
  const maxPeaks = opts.maxPeaks ?? 8;
  const now = opts.now ?? new Date();

  if (slotMinutes <= 0 || 1440 % slotMinutes !== 0) {
    throw new Error('slotMinutes must divide evenly into 1440');
  }

  const cutoff = now.getTime() - daysBack * 86_400_000;
  const slotsPerDay = 1440 / slotMinutes;

  // slotIndex → { totalMg, distinctDays: Set<localYMD> }
  const buckets = new Map<number, { totalMg: number; days: Set<string> }>();
  const distinctDayKeys = new Set<string>();
  let totalMgInWindow = 0;

  for (const d of doses) {
    const t = Date.parse(d.ts);
    if (!Number.isFinite(t) || t < cutoff || t > now.getTime()) continue;
    if (!(d.computedMg7oh > 0)) continue;
    const dt = new Date(t);
    const local = localParts(dt, tz);
    const slot = Math.floor((local.hour * 60 + local.minute) / slotMinutes);
    const dayKey = `${local.ymd}`;
    distinctDayKeys.add(dayKey);
    totalMgInWindow += d.computedMg7oh;
    let entry = buckets.get(slot);
    if (!entry) {
      entry = { totalMg: 0, days: new Set<string>() };
      buckets.set(slot, entry);
    }
    entry.totalMg += d.computedMg7oh;
    entry.days.add(dayKey);
  }

  const distinctDays = distinctDayKeys.size;
  if (distinctDays === 0 || totalMgInWindow <= 0) return [];

  // Average daily mg over the window — divide by distinct days the user
  // logged (not the full window) so a sparse logger isn't punished.
  const avgDailyMg = totalMgInWindow / distinctDays;

  const peaks: TimingPeak[] = [];
  for (const [slot, entry] of buckets) {
    if (entry.days.size < minOccurrences) continue;
    if (!(entry.totalMg > 0)) continue;
    peaks.push({
      time: slotToClock(slot, slotsPerDay),
      shareOfDailyMg: entry.totalMg / distinctDays / avgDailyMg,
      sampleSize: entry.days.size,
      totalMg: entry.totalMg,
    });
  }

  peaks.sort((a, b) => b.totalMg - a.totalMg);
  return peaks.slice(0, maxPeaks);
}

/**
 * Convert habitual peaks into a suggested schedule for a target daily mg.
 * Slots whose scaled amount rounds below the minimum-renderable dose
 * (0.5 mg) are dropped, and the remaining slots are re-normalised so the
 * final amounts sum to roughly `targetMg`.
 *
 * Returns slots sorted by clock time ascending — that's the order a user
 * will read them on the card.
 */
export function suggestSchedule(
  targetMg: number,
  peaks: readonly TimingPeak[],
): ScheduleSlot[] {
  if (!Number.isFinite(targetMg) || targetMg <= 0) return [];
  if (peaks.length === 0) return [];

  // Normalize the share vector so it sums to 1 (regardless of input).
  const shareSum = peaks.reduce((s, p) => s + Math.max(0, p.shareOfDailyMg), 0);
  if (!(shareSum > 0)) return [];

  // First pass: provisional amounts.
  const provisional = peaks.map((p) => ({
    peak: p,
    rawMg: (Math.max(0, p.shareOfDailyMg) / shareSum) * targetMg,
  }));

  // Drop slots that round below 0.5 mg.
  const kept = provisional.filter((s) => roundMg(s.rawMg) >= 0.5);
  if (kept.length === 0) return [];

  // Re-normalise so the sum approximates targetMg after rounding.
  const keptShareSum = kept.reduce((s, x) => s + x.peak.shareOfDailyMg, 0);
  const slots: ScheduleSlot[] = kept.map((x) => {
    const mg = roundMg((x.peak.shareOfDailyMg / keptShareSum) * targetMg);
    return {
      time: x.peak.time,
      mg,
      confidence: confidenceFor(x.peak.sampleSize),
    };
  });

  // After rounding, the sum might drift by a tenth or two. Nudge the
  // largest slot to absorb the rounding delta so the schedule sums to
  // exactly `targetMg` (within 0.1 mg precision).
  const sum = slots.reduce((s, x) => s + x.mg, 0);
  const delta = roundMg(targetMg - sum);
  if (Math.abs(delta) > 0 && slots.length > 0) {
    let largestIdx = 0;
    for (let i = 1; i < slots.length; i++) {
      if (slots[i].mg > slots[largestIdx].mg) largestIdx = i;
    }
    const adjusted = roundMg(slots[largestIdx].mg + delta);
    slots[largestIdx] = { ...slots[largestIdx], mg: Math.max(0.5, adjusted) };
  }

  slots.sort((a, b) => clockToMinutes(a.time) - clockToMinutes(b.time));
  return slots;
}

// --- internals -------------------------------------------------------

function confidenceFor(sampleSize: number): SlotConfidence {
  if (sampleSize >= 7) return 'high';
  if (sampleSize >= 4) return 'medium';
  return 'low';
}

function roundMg(x: number): number {
  return Math.round(x * 10) / 10;
}

function slotToClock(slot: number, slotsPerDay: number): string {
  const slotMinutes = 1440 / slotsPerDay;
  // Use the slot's leading edge — "08:00" reads more naturally than
  // the center-of-slot "08:15".
  const totalMinutes = slot * slotMinutes;
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function clockToMinutes(clock: string): number {
  const [h, m] = clock.split(':').map((s) => Number.parseInt(s, 10));
  return h * 60 + m;
}

export interface LocalParts {
  ymd: string;
  hour: number;
  minute: number;
}

/**
 * Return YYYY-MM-DD + HH + MM for the given UTC instant in the given IANA TZ.
 * Implemented with Intl.DateTimeFormat to avoid pulling in a date library.
 */
export function localParts(d: Date, tz: string): LocalParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  const y = get('year');
  const mo = get('month');
  const day = get('day');
  // Intl can emit '24' for midnight in some locales; clamp to '00'.
  const rawH = get('hour');
  const hour = rawH === '24' ? 0 : Number.parseInt(rawH, 10);
  const minute = Number.parseInt(get('minute'), 10);
  return { ymd: `${y}-${mo}-${day}`, hour, minute };
}
